import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useContext,
} from "react";
import clsx from "clsx";
import { IdType, SortingRule } from "react-table";
import { useHistory } from "react-router-dom";
import {
  HvButton,
  HvTypography,
  HvGrid,
} from "@hitachivantara/uikit-react-core";
import {
  HvTableColumnConfig,
  HvCellProps,
  HvRowInstance,
  HvTableRow,
  HvTableCell,
  useHvTable,
  useHvRowExpand,
  useHvSortBy,
  useHvPagination,
  useHvRowSelection,
  useHvBulkActions,
} from "@hitachivantara/uikit-react-lab";
import { Edit, Archive } from "@hitachivantara/uikit-react-icons";
import { useTranslation } from "react-i18next";
import { useSops, LinkedIncidentTypes, archiveSOP } from "lib/api";
import { useSnackbar } from "lib/provider/SnackbarProvider";

import ArchiveDialog from "components/common/ArchiveDialog";
import { OverflowTooltip } from "components/common";
import { stringFallback } from "lib/utils/fallbacks";
import SimpleTable, {
  textColumn,
  numberColumn,
  actionsColumn,
  expandColumn,
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
} from "components/common/SimpleTable";

import { NotificationContext } from "lib/context/NotificationContext";
import SopTableControls from "../SopTableControls";

import useStyles from "./styles";

const translationScope = "sop";

interface SopDataRow extends Record<string, unknown> {
  id: number;
  name: string;
  description: string;
  incidentTypes: LinkedIncidentTypes[];
  incidentTypesGrouped: string;
  incidentTypesCount: number;
}

const sortFieldMap = {
  name: "sop_name",
  description: "sop_desc",
};

const DEFAULT_SORT = [
  {
    id: "name",
    desc: false,
  },
];

const SopTable: React.FC = () => {
  const { t } = useTranslation(translationScope);
  const classes = useStyles();
  const history = useHistory();
  const [tableData, setTableData] = useState<SopDataRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchKey, setSearchKey] = useState("");
  const [filter, setFilter] = useState<Record<string, unknown>>({});

  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { setShowBanner, setBannerMessage } = useContext(NotificationContext);

  const columns = useMemo(
    (): HvTableColumnConfig<SopDataRow>[] => [
      expandColumn(
        {
          Header: t("columns.name") as string,
          accessor: "name",
          style: { width: "20%" },
        },
        t("labels.expandRowButtonAriaLabel"),
        t("labels.collapseRowButtonAriaLabel"),
        (row) => row.original.incidentTypes.length > 0
      ),
      textColumn({
        Header: t("columns.description") as string,
        accessor: "description",
      }),
      textColumn({
        Header: t("columns.incidentType") as string,
        accessor: "incidentTypesGrouped",
        style: { width: "30%" },
        disableSortBy: true,
      }),
      numberColumn({
        Header: t("columns.incidentTypeCount") as string,
        accessor: "incidentTypesCount",
        style: { width: 100 },
        disableSortBy: true,
      }),
      actionsColumn({
        id: "edit",
        Cell: ({ row }: HvCellProps<SopDataRow>) => {
          return (
            <HvButton
              icon
              category="ghost"
              aria-label={t("labels.editIncidentTypeButton")}
              onClick={() => history.push(`/settings/sop/${row.original.id}`)}
            >
              <Edit />
            </HvButton>
          );
        },
      }),
    ],
    [t, history]
  );

  const [selectedIds, setSelectedIds] = useState<
    Record<IdType<SopDataRow>, boolean>
  >({});

  const [page, setPage] = useState(DEFAULT_PAGE);
  const [size, setSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortBy, setSortBy] = useState<SortingRule<SopDataRow>[]>(DEFAULT_SORT);

  const sortField = sortFieldMap[sortBy[0].id as keyof typeof sortFieldMap];

  const { data, error, mutate } = useSops(
    {
      pageNr: page + 1,
      pageSize: size,
      orderBy: encodeURIComponent(sortField),
      Order: encodeURIComponent(sortBy[0].desc ? "desc" : "asc"),
      SearchKey: searchKey,
    },
    { suspense: tableData.length === 0 },
    filter,
    true
  );

  useEffect(() => {
    if (data) {
      setTableData(
        data.data.map((sop) => ({
          id: sop.pk_sop_template_id,
          name: sop.sop_name,
          description: sop.sop_desc,
          incidentTypes: sop.linked_incident_types,
          incidentTypesGrouped: sop.linked_incident_types.reduce(
            (str, incidentType: LinkedIncidentTypes) =>
              `${str}${str !== "" ? "," : ""} ${incidentType.inc_type_name}`,
            ""
          ),
          incidentTypesCount: sop.linked_incident_types.reduce(
            (count = 0, incident: LinkedIncidentTypes) =>
              count + incident.incident_count,
            0
          ),
        }))
      );
      setTotalCount(data.totalCount);
    }
  }, [data]);

  const initialState = useMemo(() => {
    const entries = tableData.map<[string, boolean]>((v) => [
      v.id.toString(),
      v.incidentTypes.length > 0,
    ]);

    return {
      pageIndex: page,
      pageSize: size,
      sortBy,
      lockedSelectionRowIds: (entries && Object.fromEntries(entries)) ?? {},
      selectedRowIds: selectedIds,
    };
  }, [tableData, sortBy, page, size, selectedIds]);

  const labels = useMemo(
    () => ({
      selectRowCheckBoxAriaLabel: t("labels.selectCheckbox"),
    }),
    [t]
  );

  const getExpandedRowContent = useCallback(
    (row: HvRowInstance<SopDataRow>) => {
      const { incidentTypes } = row.original;
      const incidentTypesRows = incidentTypes.slice(0, 4);

      return (
        <>
          {incidentTypesRows.map((incident, i) => (
            <HvTableRow
              key={`${row.original.id}-${incident.pk_inc_type_id}`}
              className={clsx(classes.expandedRow, {
                [classes.expandedRowTop]: i === 0,
                [classes.expandedRowBottom]: i === incidentTypesRows.length - 1,
              })}
            >
              <HvTableCell className={classes.expandedCell} colSpan={3} />
              <HvTableCell className={classes.expandedCell}>
                <OverflowTooltip
                  data={
                    <HvTypography
                      variant="link"
                      component="a"
                      onClick={() =>
                        history.push(
                          `/settings/incident-type/${incident.pk_inc_type_id}`
                        )
                      }
                      style={{ cursor: "pointer" }}
                    >
                      {stringFallback(incident.inc_type_name)}
                    </HvTypography>
                  }
                />
              </HvTableCell>
              <HvTableCell
                className={classes.expandedCell}
                style={{ textAlign: "right" }}
              >
                <HvTypography
                  variant="link"
                  component="a"
                  onClick={() =>
                    history.push(
                      `/settings/incident?incidentType=${incident.pk_inc_type_id}`
                    )
                  }
                  style={{ cursor: "pointer" }}
                >
                  {incident.incident_count}
                </HvTypography>
              </HvTableCell>
              <HvTableCell className={classes.expandedCell} />
            </HvTableRow>
          ))}
        </>
      );
    },
    [
      classes.expandedCell,
      classes.expandedRow,
      classes.expandedRowBottom,
      classes.expandedRowTop,
      history,
    ]
  );

  const bulkActions = useMemo(
    () => [
      {
        id: "archive",
        label: t("labels.bulkArchiveButton"),
        icon: <Archive />,
        onClick: () => setIsArchiveDialogOpen(true),
      },
    ],
    [t]
  );

  const instance = useHvTable<SopDataRow>(
    {
      columns,
      data: tableData || [],
      getRowId: useCallback((v: SopDataRow): string => `${v.id}`, []),
      manualPagination: true,
      pageCount: Math.ceil((totalCount ?? 0) / (initialState?.pageSize ?? 1)),
      initialState,
      labels,
      aditivePageBulkSelection: true,
      subtractivePageBulkDeselection: true,
      manualSortBy: true,
      disableMultiSort: true,
      disableSortRemove: true,
      disableCreateExpandButton: true,
    },
    useHvSortBy,
    useHvRowExpand,
    useHvPagination,
    useHvRowSelection,
    useHvBulkActions
  );

  const {
    state: { pageIndex, pageSize, sortBy: sortByFromTable, selectedRowIds },
  } = instance;

  useEffect(() => setPage?.(pageIndex), [pageIndex, setPage]);
  useEffect(() => setSize?.(pageSize), [pageSize, setSize]);
  useEffect(() => {
    setSortBy?.(sortByFromTable);
    setPage?.(0);
  }, [sortByFromTable, setSortBy, setPage]);
  useEffect(() => setSelectedIds(selectedRowIds), [selectedRowIds]);

  const onSearchHandler = (value: string) => {
    setSearchKey(value);
    instance.gotoPage(DEFAULT_PAGE);
  };

  const onFilterHandler = (value: string[]) => {
    setFilter({
      incidentType: {
        filterValues: value,
      },
    });
    instance.gotoPage(DEFAULT_PAGE);
  };

  const onArchiveClose = useCallback(
    (result, message, ids) => {
      setIsArchiveDialogOpen(false);

      if (ids) {
        const newSelectedIds = {
          ...selectedIds,
        };

        ids.forEach((id: string) => {
          delete newSelectedIds[id];
        });
        setSelectedIds(newSelectedIds);
      }

      if (result && ids) {
        enqueueSnackbar(message, {
          variant: "success",
        });
        setPage(DEFAULT_PAGE);
        mutate();
      } else if (message) {
        setBannerMessage(message);
        setShowBanner(true);
        mutate();
      }
    },
    [enqueueSnackbar, mutate, selectedIds, setBannerMessage, setShowBanner]
  );

  return (
    <HvGrid container spacing={2}>
      <HvGrid item xs={12}>
        <SopTableControls
          onSearch={onSearchHandler}
          onFilter={onFilterHandler}
        />
      </HvGrid>
      <HvGrid item xs={12}>
        <ArchiveDialog
          scope={translationScope}
          isOpen={isArchiveDialogOpen}
          promise={archiveSOP}
          onClose={onArchiveClose}
          ids={Object.keys(selectedRowIds)}
        />
        <SimpleTable
          instance={instance}
          bulkActions={bulkActions}
          totalCount={totalCount}
          getExpandedRowContent={getExpandedRowContent}
          loading={(!data && !error) || tableData === []}
        />
      </HvGrid>
    </HvGrid>
  );
};

export default SopTable;
